/**
 * DOCX-native editor registry.
 *
 * The template sidenav (placeholder / clause panels) is a SIBLING of the editor
 * container, not a child, so it can't reach the editor via props/refs. The
 * CKEditor path solved this with a `window.editor` global; the docx-native path
 * uses this small typed registry instead — the container registers a handle on
 * mount (keyed by document id), and sidebar panels read it to enumerate/scroll
 * placeholders via the editor's own content-control API. No CKEditor globals.
 */

import { buildPlaceholderNode, type DocxEditorRef } from '@propdocs-editor/react';
import { PLACEHOLDER_WRITE_META } from './plugins/placeholderSync';
import { TextSelection } from 'prosemirror-state';
import type { Node as PMNode } from 'prosemirror-model';

/** The stable tag every PropDocs placeholder SDT carries. */
export const PLACEHOLDER_TAG = 'propdocs:placeholder@1';
export const REQUIRED_LOCK = 'propdocs:required';

export interface DocxNativePlaceholder {
  /** Field name (SDT alias). */
  name: string;
  /** Current inner text; equals `[name]` when unfilled. */
  text: string;
  required: boolean;
  filled: boolean;
  /** PM position of the SDT (for scroll/selection). */
  pos: number;
  /** Server placeholder id, if the SDT carried one (migrated docs). */
  id: string | null;
}

export interface DocxNativeEditorHandle {
  /** Enumerate every placeholder in the live document, in doc order. */
  listPlaceholders: () => DocxNativePlaceholder[];
  /** Scroll a placeholder (by pos) into view and select it. */
  focusPlaceholder: (pos: number) => void;
  /** Scroll a placeholder into view + select it, matched by server id (uuid)
   *  first, else by field name (migrated docs carry no uuid in the body — only
   *  the alias). Drives the sidebar "View in doc" button. Returns true if found. */
  focusPlaceholderMatching?: (match: { id?: string | null; name?: string | null }) => boolean;
  /** Insert a new placeholder at the current cursor (the sidebar "+" button). */
  insertPlaceholderAtCursor: () => void;
  /** Insert a placeholder carrying a specific server uuid/name/required (the
   *  deal sidebar's create flow, which mints the row in the DB first). */
  insertPlaceholderWith?: (opts: { uuid: string; name: string; required?: boolean }) => void;
  /** Remove a placeholder's controls from the doc body, for a delete driven
   *  from the SIDEBAR (which has already deleted the relational row). Matched on
   *  the server id (uuid) or the field name — EVERY match goes, since one
   *  relational placeholder can sit in the document several times. Returns true
   *  if any were found. */
  removePlaceholderMatching?: (match: { id?: string | null; name?: string | null }) => boolean;
  /** Rename a placeholder's field in the doc body after the sidebar renamed the
   *  row: updates the alias/name and the required lock, and rewrites the
   *  empty-state `[name]` label (a filled value is left alone). Matched on the
   *  server id (uuid) or the PREVIOUS name — without this the body keeps the old
   *  alias and every later name-matched sync misses it. */
  renamePlaceholderMatching?: (
    match: { id?: string | null; name?: string | null },
    opts: { name: string; required?: boolean }
  ) => boolean;
  /** Fill a placeholder's VALUE in the doc body (replace its `[name]` inner
   *  text). Matched on the server id (uuid) or the field name — every match is
   *  filled. Pass the NAME: the uuid resolves only for a control inserted in
   *  this session (see `placeholderIdMatches`). Returns true if any were filled. */
  fillPlaceholderValue?: (match: { id?: string | null; name?: string | null }, value: string) => boolean;
  /** Subscribe to placeholder-set changes (add/remove/rename/fill). Returns an
   *  unsubscribe fn. Fires on the editor's content changes. */
  subscribe: (listener: () => void) => () => void;

  /**
   * Force the debounced autosave to complete NOW, and reconcile the relational
   * `document_suggestions` rows from the same live state.
   *
   * Every deal-flow state transition (send, ready-to-sign, request review,
   * grant access, prepare to sign) must do this first, because both the body
   * and the suggestion gates are read server-side immediately afterwards. The
   * CKEditor path did it with `window.editor.plugins.get('Autosave').save()`;
   * that global does not exist here, so those call sites silently stopped
   * flushing — changes made in the last few seconds were transmitted as if they
   * had never happened, and pending suggestions went uncounted.
   *
   * @returns the number of PENDING suggestions after reconciling, so a caller
   *          can gate on it without a second round trip.
   */
  flushPendingChanges?: () => Promise<{ pendingSuggestions: number }>;
}

const registry = new Map<string, DocxNativeEditorHandle>();
const globalListeners = new Set<(docId: string) => void>();

/** Container calls this on mount; returns an unregister fn for cleanup. */
export function registerDocxNativeEditor(docId: string, handle: DocxNativeEditorHandle): () => void {
  registry.set(docId, handle);
  globalListeners.forEach((l) => l(docId));
  return () => {
    if (registry.get(docId) === handle) registry.delete(docId);
    globalListeners.forEach((l) => l(docId));
  };
}

export function getDocxNativeEditor(docId: string): DocxNativeEditorHandle | undefined {
  return registry.get(docId);
}

/** Sidebar panels subscribe here to learn when an editor (un)registers. */
export function onRegistryChange(listener: (docId: string) => void): () => void {
  globalListeners.add(listener);
  return () => globalListeners.delete(listener);
}

/**
 * Does this placeholder SDT correspond to the sidebar row identified by
 * `match`?
 *
 * The server uuid is NOT durable in the body, so a uuid-only match reliably
 * found nothing:
 *  - `sdt.id` is OOXML's numeric `w:id`, so a uuid parked there on insert is
 *    dropped the first time the document is serialized;
 *  - migrated bodies carry it as `w:dataBinding/@w:storeItemID` (checked here),
 *    but `stripProvenanceDataBinding` removes that on every save — an xpath-less
 *    binding makes Word declare the file corrupt.
 *
 * The FIELD NAME (`w:alias`) is the identity that actually survives a round
 * trip, which is why every matcher here accepts it.
 */
function placeholderIdMatches(node: PMNode, id: string | null | undefined): boolean {
  if (id == null) return false;
  if (node.attrs.id != null && String(node.attrs.id) === String(id)) return true;
  const raw = node.attrs.dataBinding;
  if (typeof raw === 'string' && raw) {
    try {
      const binding = JSON.parse(raw) as { storeItemID?: string };
      if (binding?.storeItemID && String(binding.storeItemID) === String(id)) return true;
    } catch {
      /* malformed cache — fall through to the name match */
    }
  }
  return false;
}

function placeholderNameMatches(node: PMNode, name: string | null | undefined): boolean {
  if (name == null) return false;
  const own = String(node.attrs.placeholder ?? node.attrs.alias ?? '');
  return own.trim().toLowerCase() === String(name).trim().toLowerCase();
}

function isPlaceholderNode(node: PMNode): boolean {
  return node.type.name === 'sdt' && node.attrs.tag === PLACEHOLDER_TAG;
}

/**
 * Every placeholder the sidebar row refers to, in document order — matched on
 * the uuid OR the name, not the first of whichever hits.
 *
 * One relational placeholder is routinely dropped into a document more than
 * once ("Tenant Name" in the preamble and again in the covenant), and the
 * sidebar shows a single row for it, so all of its controls have to move
 * together. Stopping at the first match left the rest of them stale.
 */
function findPlaceholders(
  doc: PMNode,
  match: { id?: string | null; name?: string | null }
): { pos: number; node: PMNode }[] {
  const found: { pos: number; node: PMNode }[] = [];
  doc.descendants((node, pos) => {
    if (!isPlaceholderNode(node)) return true;
    if (placeholderIdMatches(node, match.id) || placeholderNameMatches(node, match.name)) {
      found.push({ pos, node });
    }
    return true;
  });
  return found;
}

/** Build a handle from a DocxEditorRef — the container's registration payload. */
export function makeHandleFromRef(
  ref: React.RefObject<DocxEditorRef | null>,
  subscribe: (listener: () => void) => () => void,
  insertPlaceholderAtCursor: () => void
): DocxNativeEditorHandle {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getView = () => (ref.current?.getEditorRef?.() as any)?.getView?.() ?? null;
  return {
    listPlaceholders: () => {
      const view = getView();
      if (!view) return [];
      const out: DocxNativePlaceholder[] = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      view.state.doc.descendants((node: any, pos: number) => {
        if (node.type.name === 'sdt' && node.attrs.tag === PLACEHOLDER_TAG) {
          const name = String(node.attrs.placeholder ?? node.attrs.alias ?? 'Field');
          const text = String(node.textContent ?? '');
          out.push({
            name,
            text,
            required: node.attrs.lock === REQUIRED_LOCK,
            filled: text !== `[${name}]` && text.trim().length > 0,
            pos,
            id: node.attrs.id != null ? String(node.attrs.id) : null,
          });
        }
        return true;
      });
      return out;
    },
    focusPlaceholder: (pos: number) => {
      ref.current?.scrollToPosition?.(pos);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const view = getView();
      if (view) {
        try {
          // Select inside the SDT so the placeholder popup opens.
          const tr = view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos + 1)));
          view.dispatch(tr.scrollIntoView());
          view.focus();
        } catch {
          /* best-effort */
        }
      }
    },
    focusPlaceholderMatching: (match) => {
      const view = getView();
      if (!view) return false;
      const found = findPlaceholders(view.state.doc, match);
      if (!found.length) return false;
      const targetPos = found[0].pos;
      ref.current?.scrollToPosition?.(targetPos);
      try {
        const tr = view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(targetPos + 1)));
        view.dispatch(tr.scrollIntoView());
        view.focus();
      } catch {
        /* best-effort */
      }
      return true;
    },
    insertPlaceholderAtCursor,
    insertPlaceholderWith: (opts) => {
      const view = getView();
      if (!view) return;
      try {
        const node = buildPlaceholderNode(view.state.schema, {
          name: opts.name,
          required: !!opts.required,
          placeholderId: opts.uuid,
        });
        if (node) {
          view.dispatch(view.state.tr.replaceSelectionWith(node, false).scrollIntoView());
          view.focus();
        }
      } catch {
        /* best-effort */
      }
    },
    removePlaceholderMatching: (match) => {
      const view = getView();
      if (!view) return false;
      const found = findPlaceholders(view.state.doc, match);
      if (!found.length) return false;
      // The sidebar has already deleted the row, so the editor must not report
      // this back as a document-side deletion.
      const tr = view.state.tr;
      for (const { pos, node } of found) {
        tr.delete(tr.mapping.map(pos), tr.mapping.map(pos + node.nodeSize));
      }
      view.dispatch(tr.setMeta(PLACEHOLDER_WRITE_META, true));
      return true;
    },
    fillPlaceholderValue: (match, value) => {
      const view = getView();
      if (!view) return false;
      const found = findPlaceholders(view.state.doc, match);
      if (!found.length) return false;
      try {
        const tr = view.state.tr;
        for (const { pos, node } of found) {
          const from = tr.mapping.map(pos + 1);
          const to = tr.mapping.map(pos + 1 + node.content.size);
          // Preserve the first inner run's marks so the value keeps the field's
          // formatting; fall back to no marks.
          let marks;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          node.descendants((c: any) => {
            if (marks === undefined && c.isText) marks = c.marks;
          });
          // An EMPTY value (e.g. a contact was UNLINKED) must NOT empty the SDT —
          // an empty content control collapses/vanishes. Instead restore the
          // placeholder's `[name]` empty-state label so the field REMAINS in the
          // document, just unfilled. A non-empty value replaces the inner text.
          const name = String(node.attrs.placeholder ?? node.attrs.alias ?? 'Field');
          const display = value && value.length ? value : `[${name}]`;
          tr.replaceWith(from, to, view.state.schema.text(display, marks));
        }
        view.dispatch(tr.setMeta(PLACEHOLDER_WRITE_META, true));
        return true;
      } catch {
        return false;
      }
    },
    renamePlaceholderMatching: (match, opts) => {
      const view = getView();
      if (!view) return false;
      const found = findPlaceholders(view.state.doc, match);
      if (!found.length) return false;
      try {
        const tr = view.state.tr;
        for (const { pos, node } of found) {
          const oldName = String(node.attrs.placeholder ?? node.attrs.alias ?? '');
          const from = tr.mapping.map(pos + 1);
          const to = tr.mapping.map(pos + 1 + node.content.size);
          // Only the empty-state LABEL follows the rename; a filled value is the
          // user's data and must survive it.
          if (node.textContent === `[${oldName}]`) {
            let marks;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            node.descendants((c: any) => {
              if (marks === undefined && c.isText) marks = c.marks;
            });
            tr.replaceWith(from, to, view.state.schema.text(`[${opts.name}]`, marks));
          }
          tr.setNodeMarkup(tr.mapping.map(pos), undefined, {
            ...node.attrs,
            alias: opts.name,
            placeholder: opts.name,
            lock: opts.required ? REQUIRED_LOCK : null,
          });
        }
        view.dispatch(tr.setMeta(PLACEHOLDER_WRITE_META, true));
        return true;
      } catch {
        return false;
      }
    },
    subscribe,
  };
}
